import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../shared/services/api/axiosInstance';

/**
 * Events tab for session detail: timeline of all events in the session.
 * Shows only event name and date/time.
 */
const EventsTab = ({ session }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!session?.sessionId) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosInstance
      .get(`/events/session/${encodeURIComponent(session.sessionId)}`, {
        params: { limit: 500, offset: 0 },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.events || [];
        setEvents(list);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to load events');
        setEvents([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [session?.sessionId]);

  const formatDateTime = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  // Timeline: oldest first (chronological)
  const sortedEvents = [...events].sort((a, b) => {
    const tA = new Date(a.timestamp).getTime();
    const tB = new Date(b.timestamp).getTime();
    return tA - tB;
  });

  if (loading) {
    return (
      <div className="px-6 py-8 text-center text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <p className="text-sm">Loading events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8 text-center text-red-600" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (sortedEvents.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
        <p className="text-sm">No events in this session.</p>
      </div>
    );
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-[#475569]">Event name</th>
              <th className="text-left px-4 py-3 font-medium text-[#475569]">Date / Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedEvents.map((event, index) => (
              <tr key={event.id ?? index} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5 text-[#0f172a]">{event.event_name ?? '—'}</td>
                <td className="px-4 py-2.5 text-[#475569]">{formatDateTime(event.timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventsTab;
